import React, { useState, useEffect } from 'react';
import api from '../api';
import PageAccessSelect from './PageAccessSelect';
import { LEGACY_PAGES } from '../pageAccess';

const IconUsers = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const IconPlus = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
       strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const IconTrash = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14H6L5 6"/>
    <path d="M10 11v6"/>
    <path d="M14 11v6"/>
    <path d="M9 6V4h6v2"/>
  </svg>
);

const ROLES = ['admin', 'editor', 'viewer'];

const EMPTY_INVITE = { email: '', role: 'viewer', allowed_pages: ['dashboard'] };

const fmtDate = (d) => new Date(d).toLocaleDateString(undefined, {
  month: 'short', day: 'numeric', year: 'numeric',
});

const fmtDateTime = (d) => new Date(d).toLocaleString(undefined, {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
});

const RoleGlyph = ({ role }) => {
  const common = {
    viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2,
    strokeLinecap: 'round', strokeLinejoin: 'round', width: 15, height: 15,
  };
  if (role === 'admin') return (
    <svg {...common}>
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
    </svg>
  );
  if (role === 'editor') return (
    <svg {...common}>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );
  return (
    <svg {...common}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
};

function ToggleSwitch({ checked, onChange, disabled }) {
  return (
    <label className="toggle-switch" style={{ opacity: disabled ? 0.5 : 1 }}>
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
      <span className="toggle-track" />
    </label>
  );
}

function UsersManagement({ token, user }) {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData]  = useState(EMPTY_INVITE);
  const [formError, setFormError] = useState('');
  const [adding, setAdding]      = useState(false);
  const [notice, setNotice]      = useState('');
  const [pages, setPages]        = useState([]);
  const [invites, setInvites]    = useState([]);
  const [inviteBusy, setInviteBusy] = useState(null);
  const [editUser, setEditUser]  = useState(null);
  const [editError, setEditError] = useState('');
  const [saving, setSaving]      = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      setError('Access denied. Admin privileges required.');
      setLoading(false);
      return;
    }
    fetchUsers();
    fetchInvites();
    api.get('/pages').then(res => setPages(res.data)).catch(() => setPages([]));
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch users');
      setLoading(false);
    }
  };

  const fetchInvites = async () => {
    try {
      const res = await api.get('/invites');
      setInvites(res.data);
    } catch (err) {
      setInvites([]);
    }
  };

  // ── Invite user ──────────────────────────────────────────────────────────
  const openInviteModal = () => {
    setFormData(EMPTY_INVITE);
    setFormError('');
    setShowModal(true);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setFormError('');
    setAdding(true);
    const email = formData.email.trim();
    try {
      await api.post('/invites', {
        email,
        role: formData.role,
        allowed_pages: formData.role === 'admin' ? [] : formData.allowed_pages,
      });
      setShowModal(false);
      setNotice(`Invitation sent to ${email}`);
      fetchInvites();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to send invitation');
    } finally {
      setAdding(false);
    }
  };

  const handleResendInvite = async (invite) => {
    setInviteBusy(invite.id);
    try {
      await api.post(`/invites/${invite.id}/resend`);
      setNotice(`Invitation re-sent to ${invite.email}`);
      fetchInvites();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to resend invitation');
    } finally {
      setInviteBusy(null);
    }
  };

  const handleRevokeInvite = async (invite) => {
    if (!window.confirm(`Revoke the invitation for "${invite.email}"? The link will stop working.`)) return;
    setInviteBusy(invite.id);
    try {
      await api.delete(`/invites/${invite.id}`);
      fetchInvites();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to revoke invitation');
    } finally {
      setInviteBusy(null);
    }
  };

  // ── Edit page access ─────────────────────────────────────────────────────
  const openEditAccess = (u) => {
    setEditError('');
    setEditUser({
      id: u.id,
      name: u.name,
      email_alerts: u.email_alerts,
      role: u.role,
      // Accounts from before page permissions start from what they saw then.
      allowed_pages: Array.isArray(u.allowed_pages) ? u.allowed_pages : LEGACY_PAGES,
    });
  };

  const handleSaveAccess = async (e) => {
    e.preventDefault();
    setEditError('');
    setSaving(true);
    try {
      const body = { role: editUser.role, email_alerts: !!editUser.email_alerts };
      if (editUser.role !== 'admin') body.allowed_pages = editUser.allowed_pages;
      await api.put(`/users/${editUser.id}`, body);
      setEditUser(null);
      setNotice(`Access updated for ${editUser.name}. Changes apply on their next sign-in.`);
      fetchUsers();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update access');
    } finally {
      setSaving(false);
    }
  };

  const pageLabels = (keys) => {
    const labels = pages.filter(p => keys.includes(p.key)).map(p => p.label);
    return labels.length ? labels.join(', ') : 'Dashboard only';
  };

  // ── Delete user ───────────────────────────────────────────────────────────
  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}"? This action cannot be undone.`)) return;
    try {
      await api.delete(`/users/${userId}`);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete user');
    }
  };

  // ── Role change ───────────────────────────────────────────────────────────
  const handleRoleChange = async (userId, newRole) => {
    try {
      const u = users.find(u => u.id === userId);
      await api.put(`/users/${userId}`, { role: newRole, email_alerts: u.email_alerts });
      fetchUsers();
    } catch (err) {
      alert('Failed to update user role');
    }
  };

  // ── Email alerts toggle ───────────────────────────────────────────────────
  const handleEmailAlertsToggle = async (userId, currentValue) => {
    try {
      const u = users.find(u => u.id === userId);
      await api.put(`/users/${userId}`, { role: u.role, email_alerts: !currentValue });
      fetchUsers();
    } catch (err) {
      alert('Failed to update email alerts');
    }
  };

  // ── Reset 2FA ─────────────────────────────────────────────────────────────
  const handleResetTotp = async (userId, userName) => {
    if (!window.confirm(`Reset two-factor authentication for "${userName}"? They will set it up again on next login.`)) return;
    try {
      await api.post(`/users/${userId}/reset-totp`);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reset 2FA');
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner" /><span>Loading users…</span></div>;
  }

  if (error) {
    return (
      <div style={{ padding: '48px 0', display: 'flex', justifyContent: 'center' }}>
        <div className="alert alert-error" style={{ maxWidth: 460 }}>{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-header-top">
          <div>
            <h1>Users Management</h1>
            <p>Manage user accounts, roles, and notification preferences</p>
          </div>
          <div className="page-actions">
            <button className="btn btn-primary" onClick={openInviteModal}>
              <IconPlus /> Invite User
            </button>
          </div>
        </div>
      </div>

      {notice && (
        <div className="alert alert-success" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{notice}</span>
          <button className="modal-close" onClick={() => setNotice('')} aria-label="Dismiss">×</button>
        </div>
      )}

      {/* Summary cards */}
      <div className="cards-grid" style={{ marginBottom: 20 }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">Total Users</span>
            <div className="card-icon card-icon-primary"><IconUsers /></div>
          </div>
          <div className="card-value">{users.length}</div>
          <div className="card-change">Active accounts</div>
        </div>
        {ROLES.map(role => (
          <div className="card" key={role}>
            <div className="card-header">
              <span className="card-title">{role.charAt(0).toUpperCase() + role.slice(1)}s</span>
            </div>
            <div className="card-value">{users.filter(u => u.role === role).length}</div>
            <div className="card-change">
              <span className={`role-badge role-${role}`}>{role}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Users table */}
      <div className="table-container">
        <div className="table-header">
          <h3>All Users</h3>
        </div>

        {users.length === 0 ? (
          <div className="table-empty">
            <div className="table-empty-icon"><IconUsers /></div>
            <div className="table-empty-title">No users found</div>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Page Access</th>
                <th>Email Alerts</th>
                <th>2FA</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: 'var(--primary-gradient)',
                        color: 'var(--on-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, flexShrink: 0,
                      }}>
                        {u.name?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        {u.name}
                        {u.id === user.id && (
                          <span style={{
                            marginLeft: 6, fontSize: 10, padding: '1px 6px',
                            background: 'var(--primary-subtle)', color: 'var(--primary)',
                            borderRadius: 'var(--radius-full)', fontWeight: 600,
                          }}>you</span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{u.email}</td>
                  <td>
                    <select
                      value={u.role}
                      onChange={e => handleRoleChange(u.id, e.target.value)}
                      disabled={u.id === user.id}
                      style={{
                        padding: '4px 24px 4px 8px', fontSize: 12, fontWeight: 600,
                        border: '1px solid var(--border)', borderRadius: 'var(--radius-full)',
                        background: 'var(--bg-input)', color: 'var(--text-primary)',
                        cursor: u.id === user.id ? 'not-allowed' : 'pointer',
                        appearance: 'none',
                        backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
                        backgroundRepeat: 'no-repeat', backgroundPosition: 'right 7px center',
                        opacity: u.id === user.id ? 0.55 : 1,
                        fontFamily: 'var(--font)',
                      }}
                    >
                      {ROLES.map(r => (
                        <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 240 }}>
                    {u.role === 'admin' ? (
                      <span style={{ color: 'var(--text-muted)' }}>All pages</span>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>
                          {Array.isArray(u.allowed_pages)
                            ? pageLabels(u.allowed_pages)
                            : <span style={{ color: 'var(--text-muted)' }}>Default (all but Users)</span>}
                        </span>
                        <button className="btn btn-secondary btn-sm" style={{ flexShrink: 0 }}
                                onClick={() => openEditAccess(u)}>
                          Edit
                        </button>
                      </div>
                    )}
                  </td>
                  <td>
                    <ToggleSwitch
                      checked={!!u.email_alerts}
                      onChange={() => handleEmailAlertsToggle(u.id, u.email_alerts)}
                    />
                  </td>
                  <td>
                    {u.email === 'admin@test.com' ? (
                      <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>Exempt</span>
                    ) : (
                      <span className={`role-badge ${u.totp_enabled ? 'role-admin' : 'role-viewer'}`}>
                        {u.totp_enabled ? 'Enabled' : 'Not set up'}
                      </span>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {fmtDate(u.created_at)}
                  </td>
                  <td>
                    {u.id === user.id ? (
                      <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>-</span>
                    ) : (
                      <div style={{ display: 'flex', gap: 6 }}>
                        {u.totp_enabled && (
                          <button className="btn btn-secondary btn-sm"
                                  onClick={() => handleResetTotp(u.id, u.name)}>
                            Reset 2FA
                          </button>
                        )}
                        <button className="btn btn-danger btn-sm"
                                onClick={() => handleDelete(u.id, u.name)}>
                          <IconTrash /> Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pending invitations */}
      <div className="table-container" style={{ marginTop: 20 }}>
        <div className="table-header">
          <h3>Pending Invitations</h3>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
            {invites.length} open
          </span>
        </div>

        {invites.length === 0 ? (
          <div className="table-empty">
            <div className="table-empty-icon"><IconUsers /></div>
            <div className="table-empty-title">No pending invitations</div>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Page Access</th>
                <th>Status</th>
                <th>Expires</th>
                <th>Invited By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.map(inv => (
                <tr key={inv.id}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{inv.email}</td>
                  <td><span className={`role-badge role-${inv.role}`}>{inv.role}</span></td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 240 }}>
                    {inv.role === 'admin' ? 'All pages' : pageLabels(inv.allowed_pages)}
                  </td>
                  <td>
                    <span className={'status-badge ' + (inv.status === 'pending' ? 'status-warning' : 'status-down')}>
                      <span className="badge-dot" />{inv.status}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtDateTime(inv.expires_at)}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{inv.invited_by_name || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" disabled={inviteBusy === inv.id}
                              onClick={() => handleResendInvite(inv)}>
                        Resend
                      </button>
                      <button className="btn btn-danger btn-sm" disabled={inviteBusy === inv.id}
                              onClick={() => handleRevokeInvite(inv)}>
                        <IconTrash /> Revoke
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Role permissions */}
      <div className="section-card">
        <h3>Role Permissions</h3>
        <div className="permission-list">
          {[
            { role: 'admin',  color: 'var(--primary-subtle)', fg: 'var(--primary)',
              desc: 'Full access: add and delete monitors, manage users, view all logs, configure system settings.' },
            { role: 'editor', color: 'var(--success-subtle)', fg: 'var(--success)',
              desc: 'Can add and edit monitors, trigger DNS checks, and view logs. Cannot manage users.' },
            { role: 'viewer', color: 'var(--bg-badge)', fg: 'var(--text-muted)',
              desc: 'Read-only access: view dashboards, monitors, and logs. Cannot make any changes.' },
          ].map(({ role, color, fg, desc }) => (
            <div className="permission-item" key={role}>
              <div className="permission-icon" style={{ background: color, color: fg }}>
                <RoleGlyph role={role} />
              </div>
              <div className="permission-text">
                <h4><span className={`role-badge role-${role}`}>{role}</span></h4>
                <p>{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Edit Access Modal ────────────────────────────────────── */}
      {editUser && (
        <div className="modal-overlay" onClick={() => setEditUser(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Access — {editUser.name}</h3>
              <button className="modal-close" onClick={() => setEditUser(null)}>×</button>
            </div>

            <div className="modal-body">
              {editError && <div className="alert alert-error">{editError}</div>}

              <form onSubmit={handleSaveAccess}>
                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={editUser.role}
                    onChange={e => setEditUser({ ...editUser, role: e.target.value })}
                  >
                    {ROLES.map(r => (
                      <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Page Access</label>
                  {editUser.role === 'admin' ? (
                    <div className="page-access-note">Admins have access to all pages.</div>
                  ) : (
                    <>
                      <PageAccessSelect
                        pages={pages}
                        value={editUser.allowed_pages}
                        onChange={keys => setEditUser({ ...editUser, allowed_pages: keys })}
                      />
                      <div className="form-hint">
                        Saving signs the user out so the new access applies on their next sign-in.
                      </div>
                    </>
                  )}
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary"
                          onClick={() => setEditUser(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── Invite User Modal ────────────────────────────────────── */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Invite User</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>

            <div className="modal-body">
              {formError && <div className="alert alert-error">{formError}</div>}

              <form onSubmit={handleInvite}>
                <div className="form-group">
                  <label>Email Address <span className="label-hint">*required</span></label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="name@company.com"
                    autoFocus
                  />
                  <div className="form-hint">
                    They'll get an email link to set their name and password. The link expires in 48 hours.
                  </div>
                </div>

                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={formData.role}
                    onChange={e => setFormData({ ...formData, role: e.target.value })}
                  >
                    {ROLES.map(r => (
                      <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Page Access</label>
                  {formData.role === 'admin' ? (
                    <div className="page-access-note">Admins have access to all pages.</div>
                  ) : (
                    <>
                      <PageAccessSelect
                        pages={pages}
                        value={formData.allowed_pages}
                        onChange={keys => setFormData({ ...formData, allowed_pages: keys })}
                      />
                      <div className="form-hint">
                        Controls which pages appear for this user. Admin-only actions stay admin-only.
                      </div>
                    </>
                  )}
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary"
                          onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={adding}>
                    {adding ? 'Sending…' : 'Send Invitation'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersManagement;
